import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom' 
import { validateRegistrationData, sendOTP, verifyOTPAndCompleteRegistration } from '../Actions/userActions';
import LoadingBox from '../Components/LoadingBox'
import MessageBox from '../Components/MessageBox'
import CategorySelector from '../Components/CategorySelector'
import logo from '../Media/logo.png'
import './RegisterScreen.css';
import { useToast } from '../Context/ToastContext';
import { useHistory } from 'react-router-dom';

export default function RegisterScreen(props) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [mobile, setMobile] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [role, setRole] = useState('user');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Retailer-specific fields
    const [ownerName, setOwnerName] = useState('');
    const [documentType, setDocumentType] = useState('MSME');
    const [documentNumber, setDocumentNumber] = useState('');
    const [documentFile, setDocumentFile] = useState(null);
    const [selectedCategories, setSelectedCategories] = useState([]);



    const redirect = props.location.search 
                    ? props.location.search.split('=')[1]
                    : '/';

   const userRegister = useSelector((state) => state.userRegister);
   const { userInfo,loading,error } = userRegister;

    const dispatch = useDispatch();
    const history = useHistory();
    const { error: showError } = useToast();

    const validateDocumentNumber = (type, number) => {
        const cleaned = number.toString().toUpperCase().replace(/\s+/g, '');
        
        // GSTIN: 15 chars: 2 digits + 5 letters + 4 digits + 1 letter + 1 alnum + Z + 1 alnum
        const gstRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[A-Z0-9]{1}Z[A-Z0-9]{1}$/;
        // MSME (Udyam): UDYAM-XX-00-0000000 pattern
        const msmeRegex = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;
        // Trade Licence: lenient: 5-25 of A-Z 0-9 dash slash
        const tradeRegex = /^[A-Z0-9-\\/]{5,25}$/;

        if (type === 'GST' && !gstRegex.test(cleaned)) {
            return "Invalid GSTIN format (e.g., 27ABCDE1234F1Z5)";
        }
        if (type === 'MSME' && !msmeRegex.test(cleaned)) {
            return "Invalid UDYAM format (e.g., UDYAM-MH-12-1234567)";
        }
        if (type === 'TRADE_LICENCE' && !tradeRegex.test(cleaned)) {
            return "Invalid Trade Licence number";
        }
        return null;
    };

    const submitHandler = async (e) => {
        e.preventDefault();
        
        // Prevent double submission
        if (isSubmitting) return;
        setIsSubmitting(true);
        
        // Mark that we are in registration flow ASAP to prevent any auto-redirects
        try { sessionStorage.setItem('pendingRegistration', '1'); } catch (_) {}

        // Enhanced validation like Android app
        if (!name.trim()) {
            showError('Please enter your full name');
            setIsSubmitting(false);
            return;
        }
        
        if (!email.trim()) {
            showError('Please enter your email');
            setIsSubmitting(false);
            return;
        }
        
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            showError('Please enter a valid email address');
            setIsSubmitting(false);
            return;
        }
        
        if (!mobile.trim()) {
            showError('Please enter your mobile number');
            setIsSubmitting(false);
            return;
        }
        
        if (!/^\d{10}$/.test(mobile)) {
            showError('Please enter a valid 10-digit mobile number');
            setIsSubmitting(false);
            return;
        }

        // Retailer-specific validation
        if (role === 'retailer') {
            if (!ownerName.trim()) {
                showError('Please enter owner name');
                setIsSubmitting(false);
                return;
            }
            
            if (!documentNumber.trim()) {
                showError('Please enter document number');
                setIsSubmitting(false);
                return;
            }
            
            const docError = validateDocumentNumber(documentType, documentNumber);
            if (docError) {
                showError(docError);
                setIsSubmitting(false);
                return;
            }
            
            if (!documentFile) {
                showError('Please upload business document (MSME/GST/Trade Licence)');
                setIsSubmitting(false);
                return;
            }
            
            if (selectedCategories.length === 0) {
                showError('Please select at least one business category');
                setIsSubmitting(false);
                return;
            }
        }
        
        if (!password) {
            showError('Please enter a password');
            setIsSubmitting(false);
            return;
        }
        
        if (password.length < 8) {
            showError('Password must be at least 8 characters long');
            setIsSubmitting(false);
            return;
        }
        
        if (password !== confirmPassword) {
            showError('Password and Confirm password do not match!');
            setIsSubmitting(false);
            return;
        }

        try {
            let coords = null;
            try {
                if (navigator && navigator.geolocation) {
                    // Pre-check permission when supported
                    let permissionState = 'prompt';
                    if (navigator.permissions && navigator.permissions.query) {
                        try {
                            const status = await navigator.permissions.query({ name: 'geolocation' });
                            permissionState = status.state;
                        } catch (_) {}
                    }
                    coords = await new Promise((resolve) => {
                        navigator.geolocation.getCurrentPosition(
                            (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                            (err) => {
                                console.warn('Geolocation error:', err && err.code, err && err.message);
                                resolve(null);
                            },
                            { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
                        );
                    });
                }
            } catch (_) {}

            const registrationData = await dispatch(validateRegistrationData(
                name, mobile, email, password, role, city, state, 
                role === 'retailer' ? {
                    ownerName,
                    documentType,
                    documentNumber: documentNumber.toUpperCase().replace(/\s+/g, ''),
                    documentFile,
                    selectedCategories
                } : null
            ));
            
            if (registrationData) {
                if (!coords) {
                    showError('Location permission is required to register. Please allow location access (or enable it in browser settings) and try again.');
                    try { sessionStorage.removeItem('pendingRegistration'); } catch (_) {}
                    setIsSubmitting(false);
                    return;
                }
                registrationData.coords = coords;
                try {
                    await dispatch(sendOTP(mobile));
                } catch (otpErr) {
                    const msg = String(otpErr?.message || '').toLowerCase();
                    if (msg.includes('timeout')) {
                        showError('Request timed out. Please check your internet connection and reCAPTCHA, then try again.');
                    } else {
                        showError(otpErr?.message || 'Failed to send OTP. Please try again.');
                    }
                    try { sessionStorage.removeItem('pendingRegistration'); } catch (_) {}
                    setIsSubmitting(false);
                    return;
                }
                props.history.push('/otp-verification', registrationData);
            }
        } catch (error) {
            console.error('Registration error:', error);
            showError('Registration failed: ' + error.message);
            try { sessionStorage.removeItem('pendingRegistration'); } catch (_) {}
            setIsSubmitting(false);
        }
    }

    useEffect(() =>{
        const inRegistration = (() => { try { return sessionStorage.getItem('pendingRegistration') === '1'; } catch (_) { return false; } })();
        if (userInfo && !inRegistration) {
            props.history.push(redirect);
        }
    },[props.history,redirect,userInfo]);

    // Clear flag on unmount if we never navigated
    useEffect(() => () => { try { sessionStorage.removeItem('pendingRegistration'); } catch (_) {} }, []);
    
    // Cleanup reCAPTCHA on component unmount
    useEffect(() => {
        return () => {
            if (window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.clear();
                } catch (e) {
                    console.log('Cleanup reCAPTCHA error:', e);
                }
                window.recaptchaVerifier = null;
            }
        };
    }, []);
    
    return (
        <div className="register-screen">
            <div className="register-container">
                <div className="register-card">
                    <div className="register-header">
                        <img src={logo} alt="Shopie Bazar Logo" className="logo" />
                        <h1>Create Account</h1>
                        <p>Join Shopie Bazar and start your journey</p>
                    </div>

                    <form className="register-form" onSubmit={submitHandler}>
                        {loading && <LoadingBox />}
                        {error && <MessageBox variant="danger">{error}</MessageBox>}

                        {/* Account Type Selection */}
                        <div className="form-group">
                            <label htmlFor="role">Account Type</label>
                            <select 
                                id="role"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="form-select"
                                required
                            >
                                <option value="user">👤 User (Customer)</option>
                                <option value="retailer">🏪 Retailer (Business)</option>
                            </select>
                        </div>

                        {/* Basic Information */}
                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="name">{role === 'retailer' ? 'Company Name' : 'Full Name'}</label>
                                <input 
                                    type="text"
                                    id="name"
                                    placeholder={role === 'retailer' ? 'Enter company name' : 'Enter your full name'}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                        </div>

                        {/* Retailer-specific Owner Name */}
                        {role === 'retailer' && (
                            <div className="form-group">
                                <label htmlFor="ownerName">Owner Name</label>
                                <input 
                                    type="text"
                                    id="ownerName"
                                    placeholder="Enter owner's full name"
                                    value={ownerName}
                                    onChange={(e) => setOwnerName(e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input 
                                    type="email"
                                    id="email"
                                    placeholder="Enter email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="form-input"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="mobile">Mobile Number</label>
                                <input 
                                    type="tel"
                                    id="mobile"
                                    placeholder="10-digit mobile number"
                                    value={mobile}
                                    onChange={(e) => setMobile(e.target.value)}
                                    className="form-input"
                                    maxLength="10"
                                    pattern="[0-9]{10}"
                                    required
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label htmlFor="city">City</label>
                                <input 
                                    type="text"
                                    id="city"
                                    placeholder="Enter your city"
                                    value={city}
                                    onChange={(e) => setCity(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="state">State</label>
                                <input 
                                    type="text"
                                    id="state"
                                    placeholder="Enter your state"
                                    value={state}
                                    onChange={(e) => setState(e.target.value)}
                                    className="form-input"
                                />
                            </div>
                        </div>

                        {/* Business Documents - Only for Retailers */}
                        {role === 'retailer' && (
                            <div className="business-section">
                                <h3>Business Verification</h3>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label htmlFor="documentType">Document Type</label>
                                        <select 
                                            id="documentType"
                                            value={documentType}
                                            onChange={(e) => setDocumentType(e.target.value)}
                                            className="form-select"
                                            required
                                        >
                                            <option value="MSME">MSME Certificate</option>
                                            <option value="GST">GST Registration</option>
                                            <option value="TRADE_LICENCE">Trade Licence</option>
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label htmlFor="documentNumber">Document Number</label>
                                        <input 
                                            type="text"
                                            id="documentNumber"
                                            placeholder={
                                                documentType === 'GST' ? 'e.g., 27ABCDE1234F1Z5' :
                                                documentType === 'MSME' ? 'e.g., UDYAM-MH-12-1234567' :
                                                'Enter document number'
                                            }
                                            value={documentNumber}
                                            onChange={(e) => setDocumentNumber(e.target.value)}
                                            className="form-input"
                                            required
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label htmlFor="documentFile">Upload Document</label>
                                    <div className="file-upload">
                                        <input 
                                            type="file"
                                            id="documentFile"
                                            accept="image/*,application/pdf"
                                            onChange={(e) => setDocumentFile(e.target.files[0])}
                                            className="file-input"
                                            required
                                        />
                                        <label htmlFor="documentFile" className="file-label">
                                            {documentFile ? documentFile.name : 'Choose file (PDF/Image)'}
                                        </label>
                                    </div>
                                    <small>Upload MSME/GST/Trade Licence document</small>
                                </div>

                                {/* Category Selection */}
                                <CategorySelector 
                                    selectedCategories={selectedCategories}
                                    onCategoryChange={setSelectedCategories}
                                />
                            </div>
                        )}

                        <div className="form-row">
                            <div className="form-group" style={{ position: 'relative' }}>
                                <label htmlFor="password">Password</label>
                                <input 
                                    type={showPassword ? 'text' : 'password'}
                                    id="password"
                                    placeholder="Enter password (min 8 characters)"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="form-input"
                                    required
                                    style={{ paddingRight: '44px' }}
                                />
                                <button
                                    type="button"
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowPassword(v => !v)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '35px',
                                        height: '28px',
                                        padding: '0 8px',
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: '#555'
                                    }}
                                >
                                    {showPassword ? (
                                        // Eye off icon
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M10.58 10.58A3 3 0 0012 15a3 3 0 002.42-4.42M9.9 5.1A10.94 10.94 0 0012 5c5 0 9.27 3.11 11 7.5a11.73 11.73 0 01-4.17 4.88M6.61 6.61A11.73 11.73 0 001 12.5 11.73 11.73 0 006.6 17.39" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        // Eye icon
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                            <div className="form-group" style={{ position: 'relative' }}>
                                <label htmlFor="confirmPassword">Confirm Password</label>
                                <input 
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    id="confirmPassword"
                                    placeholder="Confirm your password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="form-input"
                                    required
                                    style={{ paddingRight: '44px' }}
                                />
                                <button
                                    type="button"
                                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                                    onClick={() => setShowConfirmPassword(v => !v)}
                                    style={{
                                        position: 'absolute',
                                        right: '10px',
                                        top: '35px',
                                        height: '28px',
                                        padding: '0 8px',
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        color: '#555'
                                    }}
                                >
                                    {showConfirmPassword ? (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3 3l18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M10.58 10.58A3 3 0 0012 15a3 3 0 002.42-4.42M9.9 5.1A10.94 10.94 0 0012 5c5 0 9.27 3.11 11 7.5a11.73 11.73 0 01-4.17 4.88M6.61 6.61A11.73 11.73 0 001 12.5 11.73 11.73 0 006.6 17.39" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    ) : (
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* reCAPTCHA container for OTP verification */}
                        <div className="recaptcha-center">
                            <div id="recaptcha-container"></div>
                        </div>

                        <button type="submit" className="register-btn" disabled={loading || isSubmitting}>
                            {loading || isSubmitting ? 'Creating Account...' : 'Create Account'}
                        </button>

                        <div className="register-footer">
                            <p>
                                Already have an account? {' '}
                                <Link to={`/signin?redirect=${redirect}`} className="signin-link">
                                    Sign In
                                </Link>
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    )
}
